import React, { useState } from "react";

// UI - libs
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  MenuItem,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";
import { useSnackbar } from "notistack";

// Data - libs
import { useApolloClient } from "@apollo/client";
import { Location } from "../../../utilities/generated/gql-types";
import { stateAbbr } from "@safelease/service-utilities";
import SetupDataService from "../../../services/setup.service";
import { getLocationAddresses } from "../../../queries";

interface AddLocationAddressDialogProps {
  location: Location;
  open: boolean;
  onClose: () => void;
}

export default function AddLocationAddressDialog({ location, open, onClose }: AddLocationAddressDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const [loading, setLoading] = useState<boolean>(false);
  const [address, setAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [errors, setErrors] = useState<{
    address?: { msg?: string };
    city?: { msg?: string };
    state?: { msg?: string };
    postalCode?: { msg?: string };
  }>({});

  const handleCreateLocationAddress = async () => {
    setLoading(true);

    const response = await SetupDataService.addLocationAddress({
      locationId: location.id,
      relationshipId: location.relationshipId,
      address,
      city,
      state,
      primary: false,
    });

    await client.refetchQueries({ include: [getLocationAddresses] });

    if (!response.data?.errors) {
      enqueueSnackbar("Location address added.", { variant: "success" });
    } else {
      enqueueSnackbar(
        "Error adding location address: \n" +
          Object.values(response.data.errors)
            .map(({ msg }) => msg)
            .join("\n"),
        { variant: "error" },
      );
    }

    setLoading(false);

    if (Object.keys(errors).length === 0) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Add Location Address</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <DialogContentText>Add location address for {location.fullAddress}</DialogContentText>
        <TextField
          className="tw-w-full"
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          fullWidth
          margin="normal"
          sx={{ marginBottom: 1, width: "100%" }}
          helperText={errors.address?.msg}
        />
        <Grid className="split-input-fields" container spacing={2}>
          <Grid item xs={9} className="input-grid">
            <TextField
              className="tw-w-full"
              name="city"
              label={"City"}
              type="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              sx={{ marginBottom: 1, width: "100%" }}
              helperText={errors.city?.msg}
            />
          </Grid>
          <Grid item xs={3} className="input-grid">
            <FormControl fullWidth>
              <InputLabel id="state-selector-label">State</InputLabel>
              <Select
                autoWidth
                id="state-selector"
                labelId="state-selector-label"
                label="State"
                name="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                MenuProps={{
                  sx: {
                    maxHeight: 400,
                  },
                }}
              >
                {stateAbbr.map((state) => (
                  <MenuItem value={state} key={`state-${state}`}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.state?.msg}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          onClick={handleCreateLocationAddress}
          sx={{ display: "inline-block", float: "right" }}
          className="modal-login-button"
          disabled={!address || !city || !state}
          loading={loading}
        >
          Add Location Address
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
